import { Component, EventEmitter, inject, Input, Output, AfterViewChecked, ElementRef, ChangeDetectionStrategy } from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { MatCardModule } from '@angular/material/card'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatChipsModule } from '@angular/material/chips'
import { MatSelectModule } from '@angular/material/select'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatMenuModule } from '@angular/material/menu'
import { MatTooltipModule } from '@angular/material/tooltip'
import { MatDialog } from '@angular/material/dialog'
import { CdkDragHandle } from '@angular/cdk/drag-drop'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import {
  TemplateContentBlock,
  TemplateContentBlockType,
  TemplateContentBlockSource,
  CompletedTemplateContentBlockSource,
  TemplateContentBlockSourceType,
  FormattedTextBlock,
} from '../types/template.types'
import { AgendaItemSubCategory, CallResource, MinutesAnonymization, MinutesVerboseness } from '../../calls.service'
import { FormattedTextEditorComponent } from '../formatted-text-editor.component/formatted-text-editor.component'
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component'
import { ToastService } from '../../toast.service'
import { FilesizePipe } from '../../filesize.pipe'
import { SentryService } from '../../sentry.service'
import { truncateStringFromMiddle } from '../../utils'
import { LoggerService } from '../../logger.service'

export type FileUploadRequest = {
  blockId: number
  file: File
}

export type PdfPreviewRequest = {
  downloadUrl: string
  fileName: string
}

export const MAXIMUM_TOTAL_PAGES_FOR_CONTENT_BLOCK = 550

export const MAXIMUM_TOTAL_SOURCE_SIZE_FOR_CONTENT_BLOCK_BYTES = 20 * 1000 * 1000

export const MAXIMUM_TOTAL_TOKENS_FOR_CONTENT_BLOCK = 750_000

// Discussion-summary blocks don't co-send the transcript with their documents, so they get a larger budget.
export const MAXIMUM_TOTAL_TOKENS_FOR_DISCUSSION_SUMMARY_BLOCK = 900_000

export function maxTokensForContentBlock(blockType: TemplateContentBlockType): number {
  return blockType === TemplateContentBlockType.DISCUSSION_SUMMARY ? MAXIMUM_TOTAL_TOKENS_FOR_DISCUSSION_SUMMARY_BLOCK : MAXIMUM_TOTAL_TOKENS_FOR_CONTENT_BLOCK
}

export const EMPTY_PROSEMIRROR_DOC = '{"type":"doc"}'

@Component({
  selector: 'app-template-item-content-block',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatMenuModule,
    MatTooltipModule,
    CdkDragHandle,
    TranslatePipe,
    FormattedTextEditorComponent,
  ],
  templateUrl: './template-item-content-block.component.html',
  styleUrls: ['./template-item-content-block.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [FilesizePipe],
})
export class TemplateItemContentBlockComponent implements AfterViewChecked {
  @Input({ required: true })
  block!: TemplateContentBlock
  @Input({ required: true })
  availableResources!: CallResource[]
  @Input()
  allowFileUpload = true
  @Input()
  shouldFocus = false
  @Input()
  supportModeEnabled = false

  @Output()
  contentChanged = new EventEmitter<void>()
  @Output()
  contentChangedInstant = new EventEmitter<void>()
  @Output()
  blockRemoved = new EventEmitter<number>()
  @Output()
  fileUploadRequested = new EventEmitter<FileUploadRequest>()
  @Output()
  pdfPreviewRequested = new EventEmitter<PdfPreviewRequest>()

  dialog = inject(MatDialog)
  translateService = inject(TranslateService)
  toastService = inject(ToastService)
  fileSizePipe = inject(FilesizePipe)
  elementRef = inject(ElementRef)
  loggerService = inject(LoggerService)
  sentryService = inject(SentryService)

  TemplateContentBlockType = TemplateContentBlockType
  TemplateContentBlockSourceType = TemplateContentBlockSourceType
  MinutesVerboseness = MinutesVerboseness
  MinutesAnonymization = MinutesAnonymization
  AgendaItemSubCategory = AgendaItemSubCategory

  readonly maximumTotalSourceSizeForContentBlock = MAXIMUM_TOTAL_SOURCE_SIZE_FOR_CONTENT_BLOCK_BYTES
  readonly maximumEstimatedSourceSizeForContentBlockPreCheck = 25 * 1000 * 1000
  readonly maximumTotalPagesForContentBlock = MAXIMUM_TOTAL_PAGES_FOR_CONTENT_BLOCK

  get maximumTotalTokensForContentBlock(): number {
    return maxTokensForContentBlock(this.block.type)
  }

  ngAfterViewChecked(): void {
    if (this.shouldFocus) {
      const textarea = this.elementRef.nativeElement.querySelector(`#textarea-block-${this.block.id}`)
      if (textarea !== null) {
        setTimeout(() => {
          textarea.focus()
        }, 0)
      }
    }
  }

  markAsChanged() {
    this.contentChanged.emit()
  }

  markAsChangedInstant() {
    this.contentChangedInstant.emit()
  }

  removeContentBlock() {
    const blockTypeName = this.translateService.instant(`TEMPLATES.EDITOR.BLOCK_TYPES.${this.block.type}`)
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('TEMPLATES.EDITOR.REMOVE_CONTENT_BLOCK_DIALOG.TITLE'),
        description: this.translateService.instant('TEMPLATES.EDITOR.REMOVE_CONTENT_BLOCK_DIALOG.DESCRIPTION', { blockType: blockTypeName }),
        confirmLabel: this.translateService.instant('COMMON.DELETE'),
        cancelLabel: this.translateService.instant('COMMON.CANCEL'),
      },
    })

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.blockRemoved.emit(this.block.id)
      }
    })
  }

  get canRemoveTranscriptSource(): boolean {
    return (
      this.block.type !== TemplateContentBlockType.DISCUSSION_SUMMARY &&
      this.block.type !== TemplateContentBlockType.DISCUSSION_CHALLENGE &&
      this.block.type !== TemplateContentBlockType.MATTERS_ARISING &&
      this.block.type !== TemplateContentBlockType.BINARY
    )
  }

  addSource(sourceType: TemplateContentBlockSourceType, resource: CallResource | null) {
    if (sourceType === TemplateContentBlockSourceType.FILE_UPLOAD && resource !== null) {
      const currentSize = this.totalSizeOfSources
      const newSize = currentSize + resource.size
      if (newSize > this.maximumTotalSourceSizeForContentBlock) {
        this.toastService.showRaw(
          this.translateService.instant('TEMPLATES.EDITOR.CONTENT_BLOCK_SIZE_LIMIT_EXCEEDED', {
            maxSize: this.fileSizePipe.transform(this.maximumTotalSourceSizeForContentBlock),
          })
        )
        this.reportBlockRejection('block_size_budget', {
          currentSizeBytes: currentSize,
          resourceSizeBytes: resource.size,
          maxSizeBytes: this.maximumTotalSourceSizeForContentBlock,
        })
        return
      }
      const currentPages = this.totalNumberOfPagesOfSources
      const resourcePages = resource.metadata?.numberOfPages ?? 0
      if (currentPages + resourcePages > MAXIMUM_TOTAL_PAGES_FOR_CONTENT_BLOCK) {
        this.toastService.showRaw(
          this.translateService.instant('TEMPLATES.EDITOR.CONTENT_BLOCK_PAGE_LIMIT_EXCEEDED', {
            maxPages: MAXIMUM_TOTAL_PAGES_FOR_CONTENT_BLOCK,
          })
        )
        this.reportBlockRejection('block_page_budget', {
          currentPages,
          resourcePages,
          maxPages: MAXIMUM_TOTAL_PAGES_FOR_CONTENT_BLOCK,
        })
        return
      }
      if (this.wouldExceedTokenBudget(resource)) {
        this.toastService.showRaw(this.translateService.instant(this.tokenLimitExceededMessageKey()))
        this.reportBlockRejection('block_token_budget', {
          currentTokens: this.totalTokensOfSources,
          resourceTokens: resource.metadata?.tokenCount ?? 0,
          maxTokens: this.maximumTotalTokensForContentBlock,
        })
        return
      }
    }
    const newSource: CompletedTemplateContentBlockSource = {
      type: sourceType,
      templateContentBlockId: this.block.id,
      callResourceId: resource !== null ? resource.id : null,
      callResource: resource,
      isPending: false,
    }
    this.block.sources.push(newSource)
    this.markAsChangedInstant()
  }

  removeSource(source: TemplateContentBlockSource) {
    if (source.isPending) {
      this.block.sources = this.block.sources.filter((s) => !(s.isPending && s.fileName === source.fileName))
    } else {
      this.block.sources = this.block.sources.filter((s) => s.type !== source.type || s.callResourceId !== source.callResourceId)
    }
    this.markAsChangedInstant()
  }

  addFormattedTextBlock() {
    if (this.block.type !== TemplateContentBlockType.BINARY) {
      return
    }
    const maxIndex = this.block.data.formattedTextBlocks.length > 0 ? Math.max(...this.block.data.formattedTextBlocks.map((ftb) => ftb.index)) : -1
    this.block.data.formattedTextBlocks.push({
      content: EMPTY_PROSEMIRROR_DOC,
      index: maxIndex + 1,
    })
    this.markAsChangedInstant()
  }

  removeFormattedTextBlock(textBlockIndex: number) {
    if (this.block.type !== TemplateContentBlockType.BINARY) {
      return
    }
    this.block.data.formattedTextBlocks = this.block.data.formattedTextBlocks
      .filter((formattedTextBlock) => formattedTextBlock.index !== textBlockIndex)
      .map((formattedTextBlock, formattedTextBlockIndex) => ({ ...formattedTextBlock, index: formattedTextBlockIndex }))
    this.markAsChangedInstant()
  }

  changeFormattedTextBlockContent(textBlock: FormattedTextBlock, content: any) {
    textBlock.content = JSON.stringify(content)
    this.markAsChanged()
  }

  parseJsonStringToProseMirrorDoc(jsonString: string | null): any {
    if (jsonString === null) {
      return null
    }
    try {
      return JSON.parse(jsonString)
    } catch (error) {
      console.error('Failed to parse JSON string:', error)
      return null
    }
  }

  get showsStyleSettings(): boolean {
    return this.block.type !== TemplateContentBlockType.FIXED_TEXT && this.block.type !== TemplateContentBlockType.VARIABLE_TEXT
  }

  get showsSourceSettings(): boolean {
    return this.block.type !== TemplateContentBlockType.FIXED_TEXT
  }

  get allowSourceEditing(): boolean {
    return this.block.type !== TemplateContentBlockType.FIXED_TEXT && this.block.type !== TemplateContentBlockType.BINARY
  }

  get blockHasTranscriptSource(): boolean {
    return this.block.sources.some((source) => source.type === TemplateContentBlockSourceType.TRANSCRIPT)
  }

  get unusedAvailableResourcesForContentBlock(): CallResource[] {
    return this.availableResources.filter((resource) => !this.block.sources.some((s) => s.callResourceId === resource.id))
  }

  get nonOrphanSources(): TemplateContentBlockSource[] {
    return this.block.sources.filter((source) => {
      if (source.type === TemplateContentBlockSourceType.TRANSCRIPT) {
        return true
      }
      if (source.isPending === true) {
        return true
      }
      if (source.callResourceId === null) {
        return false
      }
      return source.callResource !== null || this.availableResources.some((r) => r.id === source.callResourceId)
    })
  }

  get totalSizeOfSources(): number {
    return this.block.sources.reduce((total, source) => {
      if (!source.isPending && source.type === TemplateContentBlockSourceType.FILE_UPLOAD && source.callResource !== null) {
        return total + source.callResource.size
      }
      return total
    }, 0)
  }

  get totalNumberOfPagesOfSources(): number {
    return this.block.sources.reduce((total, source) => {
      if (
        !source.isPending &&
        source.type === TemplateContentBlockSourceType.FILE_UPLOAD &&
        source.callResource !== null &&
        source.callResource.metadata !== null
      ) {
        return total + source.callResource.metadata.numberOfPages
      }
      return total
    }, 0)
  }

  get totalTokensOfSources(): number {
    return this.block.sources.reduce((total, source) => {
      if (
        !source.isPending &&
        source.type === TemplateContentBlockSourceType.FILE_UPLOAD &&
        source.callResource !== null &&
        source.callResource.metadata !== null
      ) {
        return total + (source.callResource.metadata.tokenCount ?? 0)
      }
      return total
    }, 0)
  }

  get hasSourcesMissingTokenCount(): boolean {
    return this.block.sources.some(
      (source) =>
        !source.isPending &&
        source.type === TemplateContentBlockSourceType.FILE_UPLOAD &&
        source.callResource !== null &&
        source.callResource.metadata !== null &&
        source.callResource.metadata.tokenCount === null
    )
  }

  private wouldExceedTokenBudget(resource: CallResource): boolean {
    return this.totalTokensOfSources + (resource.metadata?.tokenCount ?? 0) > this.maximumTotalTokensForContentBlock
  }

  private tokenLimitExceededMessageKey(): string {
    return this.totalTokensOfSources === 0
      ? 'TEMPLATES.EDITOR.CONTENT_BLOCK_TOKEN_LIMIT_EXCEEDED_SINGLE'
      : 'TEMPLATES.EDITOR.CONTENT_BLOCK_TOKEN_LIMIT_EXCEEDED'
  }

  get contentCapacityPercentage(): number {
    const sizeRatio = this.totalSizeOfSources / this.maximumTotalSourceSizeForContentBlock
    const tokenRatio = this.totalTokensOfSources / this.maximumTotalTokensForContentBlock
    const pageRatio = this.totalNumberOfPagesOfSources / this.maximumTotalPagesForContentBlock
    return Math.min(100, Math.round(Math.max(sizeRatio, tokenRatio, pageRatio) * 100))
  }

  get contentCapacityBreakdown(): string {
    const translationKey = this.hasSourcesMissingTokenCount
      ? 'TEMPLATES.EDITOR.CONTENT_CAPACITY_BREAKDOWN_NO_TOKENS'
      : 'TEMPLATES.EDITOR.CONTENT_CAPACITY_BREAKDOWN'
    return this.translateService.instant(translationKey, {
      pages: this.totalNumberOfPagesOfSources,
      maxPages: this.maximumTotalPagesForContentBlock,
      size: this.fileSizePipe.transform(this.totalSizeOfSources),
      maxSize: this.fileSizePipe.transform(this.maximumTotalSourceSizeForContentBlock),
      tokens: this.totalTokensOfSources.toLocaleString(),
      maxTokens: this.maximumTotalTokensForContentBlock.toLocaleString(),
    })
  }

  get displayMissingSourceWarning(): boolean {
    return this.block.type !== TemplateContentBlockType.FIXED_TEXT && this.block.sources.length === 0
  }

  canAddResource(resource: CallResource): boolean {
    const currentSize = this.totalSizeOfSources
    const newSize = currentSize + resource.size
    if (newSize > this.maximumTotalSourceSizeForContentBlock) {
      return false
    }

    const currentPages = this.totalNumberOfPagesOfSources
    const resourcePages = resource.metadata?.numberOfPages ?? 0
    const newPages = currentPages + resourcePages
    if (newPages > MAXIMUM_TOTAL_PAGES_FOR_CONTENT_BLOCK) {
      return false
    }

    if (this.wouldExceedTokenBudget(resource)) {
      return false
    }
    return true
  }

  truncateFileNameFromMiddle(fileName: string, maxLength: number = 20): string {
    return truncateStringFromMiddle(fileName, maxLength)
  }

  removeFileExtension(fileName: string): string {
    return fileName.replace(/\.[^/.]+$/, '')
  }

  onResourceMenuItemClick(event: MouseEvent, resource: CallResource): void {
    if (!this.canAddResource(resource)) {
      event.stopPropagation()
      return
    }
    this.addSource(TemplateContentBlockSourceType.FILE_UPLOAD, resource)
  }

  getResourceDisabledTooltip(resource: CallResource): string {
    const currentSize = this.totalSizeOfSources
    const newSize = currentSize + resource.size
    if (newSize > this.maximumTotalSourceSizeForContentBlock) {
      return this.translateService.instant('TEMPLATES.EDITOR.CONTENT_BLOCK_SIZE_LIMIT_EXCEEDED', {
        maxSize: this.fileSizePipe.transform(this.maximumTotalSourceSizeForContentBlock),
      })
    }

    const currentPages = this.totalNumberOfPagesOfSources
    const resourcePages = resource.metadata?.numberOfPages ?? 0
    const newPages = currentPages + resourcePages
    if (newPages > MAXIMUM_TOTAL_PAGES_FOR_CONTENT_BLOCK) {
      return this.translateService.instant('TEMPLATES.EDITOR.CONTENT_BLOCK_PAGE_LIMIT_EXCEEDED', {
        maxPages: MAXIMUM_TOTAL_PAGES_FOR_CONTENT_BLOCK,
      })
    }

    if (this.wouldExceedTokenBudget(resource)) {
      return this.translateService.instant(this.tokenLimitExceededMessageKey())
    }

    return ''
  }

  triggerFileUpload() {
    const fileInput = document.getElementById(`file-upload-${this.block.id}`)
    if (fileInput !== null) {
      fileInput.click()
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement
    if (input.files === null || input.files.length === 0) {
      return
    }
    const file = input.files[0]
    const currentSize = this.totalSizeOfSources
    const newSize = currentSize + file.size
    if (newSize > this.maximumEstimatedSourceSizeForContentBlockPreCheck) {
      const formattedSize = this.fileSizePipe.transform(this.maximumEstimatedSourceSizeForContentBlockPreCheck)
      this.toastService.showRaw(this.translateService.instant('TEMPLATES.EDITOR.CONTENT_BLOCK_SIZE_LIMIT_EXCEEDED', { maxSize: formattedSize }))
      this.reportBlockRejection('block_size_budget', {
        currentSizeBytes: currentSize,
        resourceSizeBytes: file.size,
        maxSizeBytes: this.maximumEstimatedSourceSizeForContentBlockPreCheck,
      })
      input.value = ''
      return
    }
    this.fileUploadRequested.emit({ blockId: this.block.id, file })
    input.value = ''
  }

  private reportBlockRejection(reason: string, extra: Record<string, number>): void {
    this.sentryService.captureMessage('Content-block document upload rejected', {
      level: 'warning',
      tags: { feature: 'document-upload' },
      extra: { reason, ...extra },
    })
  }

  openPdfPreview(source: TemplateContentBlockSource): void {
    if (source.isPending) {
      return
    }
    const resource = this.availableResources.find((resource) => resource.id === source.callResourceId) ?? null
    if (resource === null || resource.downloadUrl === null) {
      this.loggerService.error('Resource not found or download URL is missing. Cannot open PDF preview.')
      return
    }
    this.pdfPreviewRequested.emit({
      downloadUrl: resource.downloadUrl,
      fileName: resource.fileName,
    })
  }
}
